import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Calling from "../../api/Calling";
import {
  AiOutlinePlus,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineMore,
} from "react-icons/ai";
import { useDispatch } from "react-redux";
import { addProfile, removeProfile } from "../../redux/slices/profileSlices";
import { FaUserLock } from "react-icons/fa";

const AllProfile = () => {
  const [loader, setLoader] = useState(true);
  const [allProfile, setAllProfile] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState("");
  const [deleteProfileId, setDeleteProfileId] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [editProfileId, setEditProfileId] = useState(null);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  // Create refs for file inputs
  const createFileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getAllProfile();
      if (resp.status) {
        setAllProfile(resp?.data || []);
      } else {
        setAllProfile([]);
      }
    } catch (error) {
      console.error(error);
      navigate("/error");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenuId && !event.target.closest(".menu-container")) {
        setActiveMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeMenuId]);

  const resetFormState = () => {
    setProfileName("");
    setProfileImage(null);
    setSelectedPreview(null);
    setError("");
    setEditProfileId(null);
    setIsEditingImage(false);
  };

  const handleCreateProfile = async () => {
    if (!profileName.trim()) {
      setError("App name is required.");
      return;
    }
    if (!profileImage) {
      setError("App icone is required.");
      return;
    }
    if (profileImage.size > 1024 * 1024) {
      setError("Image size must be less than 1MB.");
      return;
    }
    const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
    if (!allowedTypes.includes(profileImage.type)) {
      setError("Only PNG, JPG, and JPEG files are allowed.");
      return;
    }

    const formData = new FormData();
    formData.append("p_name", profileName.trim());
    formData.append("image", profileImage);

    try {
      const resp = await Calling.addProfile(formData);
      if (resp.status) {
        setShowCreateModal(false);
        resetFormState();
        fetchData();
      } else {
        setError(resp.message || "Failed to create app.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong!");
    }
  };

  const handleEditProfile = async () => {
    if (!profileName.trim()) {
      setError("App icone is required.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("p_name", profileName.trim());

      if (profileImage) {
        if (profileImage.size > 1024 * 1024) {
          setError("Image size must be less than 1MB.");
          return;
        }
        const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
        if (!allowedTypes.includes(profileImage.type)) {
          setError("Only PNG, JPG, and JPEG files are allowed.");
          return;
        }
        formData.append("image", profileImage);
      }

      const resp = await Calling.updateProfile(editProfileId, formData);
      if (resp.status) {
        setShowEditModal(false);
        resetFormState();
        fetchData();
      } else {
        setError(resp.message || "Failed to update app.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong!");
    }
  };

  const handleDeleteProfile = async () => {
    try {
      const resp = await Calling.deleteProfile(deleteProfileId);
      if (resp.status) {
        setShowDeleteModal(false);
        fetchData();
      } else {
        setError(resp.message || "Failed to delete app.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong!");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedPreview(reader.result);
      };
      reader.readAsDataURL(file);
      if (showEditModal) {
        setIsEditingImage(true);
      }
    }
  };

  const prepareEdit = (profile) => {
    setEditProfileId(profile._id);
    setProfileName(profile.p_name);
    setSelectedPreview(profile.p_icon);
    setShowEditModal(true);
  };

  const toggleMenu = (profileId) => {
    if (activeMenuId === profileId) {
      setActiveMenuId(null);
    } else {
      setActiveMenuId(profileId);
    }
  };

  // Handle click on edit image button
  const handleEditImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editFileInputRef.current) {
      editFileInputRef.current.click();
    }
  };

  const handleSetProfile = (profile) => {
    localStorage.setItem("profile", profile?._id);
    dispatch(addProfile(profile));
    navigate(`/`);
  };

  return (
    <div className="min-h-screen w-full z-50 bg-black text-white flex flex-col items-center py-10">
      {/* Logout Button */}
      <div className="absolute top-5 right-5 flex flex-col items-end gap-2">
        <Link
          to="/manage/changePw/"
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-all flex items-center gap-2"
        >
          <FaUserLock />
          Change Password
        </Link>

        <Link
          to="/logout"
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-all"
        >
          Logout
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-10 mt-20">Who's Login?</h1>

      {/* Loader: Displayed when the data is loading */}
      {loader ? (
        <div className="flex justify-center items-center min-h-screen w-full bg-black">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white border-opacity-80"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 px-5">
          {allProfile.map((profile) => (
            <div
              key={profile._id}
              className="flex flex-col items-center group cursor-pointer relative"
              onClick={() => handleSetProfile(profile)}
            >
              <div className="relative overflow-hidden rounded-md">
                <img
                  src={profile?.p_icon}
                  alt={profile?.p_name}
                  className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-cover rounded-md hover:scale-110 transition-all duration-300"
                />
                {/* Three dots menu button */}
                <div
                  className="absolute top-2 right-2 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu(profile._id);
                  }}
                >
                  <div className="bg-black/50 rounded-full p-1 cursor-pointer hover:scale-110 transition-all">
                    <AiOutlineMore className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Menu dropdown */}
                {activeMenuId === profile._id && (
                  <div className="absolute top-10 right-2 bg-gray-800 rounded-md shadow-lg menu-container">
                    <div className="py-1">
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 flex items-center space-x-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          prepareEdit(profile);
                          setActiveMenuId(null);
                        }}
                      >
                        <AiOutlineEdit className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 flex items-center space-x-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteProfileId(profile._id);
                          setShowDeleteModal(true);
                          setActiveMenuId(null);
                        }}
                      >
                        <AiOutlineDelete className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <span className="mt-3 text-center font-medium hover:scale-105 transition-all">
                {profile?.p_name}
              </span>
            </div>
          ))}

          {/* Create Profile Card */}
          <div
            className="flex flex-col items-center cursor-pointer hover:scale-105 transition-all"
            onClick={() => {
              resetFormState();
              setShowCreateModal(true);
            }}
          >
            <div className="flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-gray-700 rounded-md hover:bg-gray-600 transition-all duration-300 border-2 border-dashed border-gray-500">
              <AiOutlinePlus className="w-10 h-10 text-white" />
            </div>
            <span className="mt-3 text-center font-medium">Create App</span>
          </div>
        </div>
      )}

      {/* Create Profile Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 backdrop-blur-sm"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-gray-800 text-white p-6 rounded-xl w-80 space-y-4 relative border border-gray-700 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-center">Create App</h2>

            {error && (
              <p className="text-red-400 text-sm bg-red-400/20 p-2 rounded">
                {error}
              </p>
            )}

            <div>
              <label className="text-sm text-gray-300 mb-1 block">
                App Name
              </label>
              <input
                type="text"
                placeholder="Enter Name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="border border-gray-600 bg-gray-700 w-full p-2 rounded mt-1 focus:outline-none focus:border-blue-500 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-300 mb-1 block">
                App Picture
              </label>

              {selectedPreview ? (
                <div className="relative w-32 h-32 mx-auto">
                  <img
                    src={selectedPreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-md"
                  />
                  <button
                    onClick={() => {
                      setSelectedPreview(null);
                      setProfileImage(null);
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                  >
                    <AiOutlineDelete className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-500 rounded-md p-4 text-center">
                  <input
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    onChange={handleFileChange}
                    className="hidden"
                    ref={createFileInputRef}
                  />
                  <button
                    className="cursor-pointer block text-blue-400 hover:text-blue-300 w-full"
                    onClick={() => createFileInputRef.current?.click()}
                  >
                    <AiOutlinePlus className="w-10 h-10 mx-auto mb-2" />
                    <span>Choose an image</span>
                    <p className="text-xs text-gray-400 mt-1">
                      PNG, JPG (Max 1MB)
                    </p>
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={handleCreateProfile}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-all duration-300 hover:scale-105"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetFormState();
                }}
                className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-all duration-300 hover:scale-105"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 backdrop-blur-sm"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-gray-800 text-white p-6 rounded-xl w-80 space-y-4 relative border border-gray-700 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-center">Edit App</h2>

            {error && (
              <p className="text-red-400 text-sm bg-red-400/20 p-2 rounded">
                {error}
              </p>
            )}

            <div>
              <label className="text-sm text-gray-300 mb-1 block">
                App Name
              </label>
              <input
                type="text"
                placeholder="Enter Name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="border border-gray-600 bg-gray-700 w-full p-2 rounded mt-1 focus:outline-none focus:border-blue-500 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-300 mb-1 block">
                App icon
              </label>

              {selectedPreview ? (
                <div className="relative w-32 h-32 mx-auto">
                  <img
                    src={selectedPreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-md"
                  />
                  <button
                    onClick={handleEditImageClick}
                    className="absolute bottom-2 right-2 bg-blue-500 rounded-full p-1 hover:bg-blue-600"
                  >
                    <AiOutlineEdit className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-500 rounded-md p-4 text-center">
                  <input
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    onChange={handleFileChange}
                    className="hidden"
                    ref={editFileInputRef}
                  />
                  <button
                    className="cursor-pointer block text-blue-400 hover:text-blue-300 w-full"
                    onClick={() => editFileInputRef.current?.click()}
                  >
                    <AiOutlinePlus className="w-10 h-10 mx-auto mb-2" />
                    <span>Choose an image</span>
                    <p className="text-xs text-gray-400 mt-1">
                      PNG, JPG (Max 1MB)
                    </p>
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                onChange={handleFileChange}
                className="hidden"
                ref={editFileInputRef}
              />
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={handleEditProfile}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-all duration-300 hover:scale-105"
              >
                Update
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetFormState();
                }}
                className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-all duration-300 hover:scale-105"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Profile Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 backdrop-blur-sm"
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="bg-gray-800 text-white p-6 rounded-xl w-96 space-y-4 relative border border-gray-700 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center mb-2">
              <div className="bg-red-500/20 p-3 rounded-full">
                <AiOutlineDelete className="w-8 h-8 text-red-500" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center">Delete App</h2>

            <div className="bg-gray-700/50 p-4 rounded-lg border border-gray-600">
              <p className="text-center text-gray-300 mb-2">
                <span className="font-medium text-white">Warning:</span> This
                action cannot be undone.
              </p>
              <p className="text-center text-gray-300">
                Deleting this app will permanently remove:
              </p>
              <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                <li>App information</li>
                <li>Saved preferences</li>
                <li>App picture</li>
                <li>All associated data</li>
              </ul>
            </div>

            <p className="text-center text-gray-400 text-sm">
              Please confirm if you want to proceed with deletion
            </p>

            <div className="flex justify-between mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-600 text-white py-2 px-6 rounded-lg hover:bg-gray-700 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProfile}
                className="bg-red-600 text-white py-2 px-6 rounded-lg hover:bg-red-700 transition-all duration-300 flex items-center space-x-2"
              >
                <AiOutlineDelete className="w-4 h-4" />
                <span>Delete App</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProfile;
